import React, { useState } from "react";
import { Box, Button, Card, Stack, Typography, Alert } from "@mui/material";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { AuthCardTopBar, AuthCardIntro } from "./AuthCardHeader";
import { ConsentPopup } from "./ConsentPopup";
import { ConsentFormData } from "../types";
import { useUpdateConsent } from "../mutations";

interface ConsentDeclinedMessageProps {
  onBackToLogin: () => void;
  onConsentChanged?: () => void;
}

export const ConsentDeclinedMessage: React.FC<ConsentDeclinedMessageProps> = ({
  onBackToLogin,
  onConsentChanged,
}) => {
  const [showConsentPopup, setShowConsentPopup] = useState(false);
  const updateConsentMutation = useUpdateConsent();

  const handleChangeConsent = () => {
    setShowConsentPopup(true);
  };

  const handleConsentSubmit = async (data: ConsentFormData) => {
    try {
      await updateConsentMutation.mutateAsync({
        privacyPolicyAcknowledged: data.privacyPolicyAcknowledged,
        decision: data.option,
        confirmationText: data.confirmationText,
      });
      setShowConsentPopup(false);
      if (data.option === "consent") {
        onConsentChanged?.();
      }
    } catch (error) {
      console.error("Failed to update consent:", error);
    }
  };

  const handleConsentClose = () => {
    setShowConsentPopup(false);
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: "divider",
          borderRadius: 4,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 4,
          }}
        >
          <Stack spacing={4}>
            <AuthCardTopBar />
            <AuthCardIntro
              title="Consent Required"
              subtitle="To access the PEAK4 programme, you must provide consent for data collection and monitoring."
            />

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                You have previously declined consent for the PEAK4 programme.
                Consent is required to proceed with the programme and access
                your dashboard.
              </Typography>
            </Alert>

            <Stack spacing={2} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  bgcolor: "error.light",
                  color: "error.contrastText",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <BlockRoundedIcon sx={{ fontSize: 40 }} />
              </Box>

              <Typography variant="h6" component="h2" fontWeight={600}>
                Access Restricted
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Without consent, we cannot provide access to the PEAK4 programme
                features. If you change your mind, please contact your
                administrator or support team.
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <Button
                size="large"
                variant="contained"
                fullWidth
                startIcon={<EditRoundedIcon />}
                onClick={handleChangeConsent}
                disabled={updateConsentMutation.isPending}
                sx={{ mt: 2 }}
              >
                {updateConsentMutation.isPending
                  ? "Updating..."
                  : "Change Consent"}
              </Button>

              <Button
                size="large"
                variant="outlined"
                fullWidth
                onClick={onBackToLogin}
              >
                Return to Login
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Need help? Contact{" "}
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{ fontWeight: 500 }}
                >
                  dataprivacy@peak4.co.uk
                </Typography>
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Card>

      <ConsentPopup
        open={showConsentPopup}
        onClose={handleConsentClose}
        onSubmit={handleConsentSubmit}
      />
    </>
  );
};
